import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { useTranslation } from "react-i18next";
import { localized, useAppLanguage } from "../../../../utils/localized";
import { RootState } from "../../../../store";
import { impactOccuer } from "../../../../utils/hapticEvents";

import { Card } from "../../../../components/ui/card";

import { formatPrice } from "../../../../utils/getPrice";
import { Button } from "../../../../components/ui/button";
import { Minus } from "../../../../assets/Minus";
import { PlusIcon } from "../../../../assets/Plus";

import { IProductInfo } from "../../../home/types/Menu";
import {
  buildCartKey,
  buildModifierPayload,
  getChoosableGroups,
  getDefaultSelections,
  getModifierTotal,
} from "../../../home/libs/modifiers";
import { cn } from "../../../../lib/utils";
import { useAddToBasket } from "../../../basket/services/addToBasket";
import { useDecreaseFromBasket } from "../../../basket/services/decreaseFromBasket";
import { useCartLimitReached } from "../../../basket/services/useCartLimit";
import { ProductImage } from "../../../../components/ProductImage";

type IProps = {
  product: IProductInfo;
  isNavigate?: boolean;
};

// Авторские блюда рисуются градиентной карточкой: белая заливка с цветным
// подтоном снизу и градиентная рамка. Цвет — свой у каждого автора.
const AUTHOR_CARD_STYLE: Record<string, React.CSSProperties> = {
  husnora: {
    background: `
      linear-gradient(180.06deg, rgba(255, 255, 255, 0.2) 55.53%, rgba(228, 0, 88, 0.2) 99.95%) padding-box,
      linear-gradient(0deg, #FFFFFF, #FFFFFF) padding-box,
      linear-gradient(180deg, rgba(240, 242, 243, 0) 0%, #E40058 100%) border-box
    `,
  },
  tohir: {
    background: `
      linear-gradient(180.06deg, rgba(255, 255, 255, 0.2) 55.53%, rgba(0, 120, 84, 0.2) 99.95%) padding-box,
      linear-gradient(0deg, #FFFFFF, #FFFFFF) padding-box,
      linear-gradient(180deg, rgba(240, 242, 243, 0) 0%, #002100 100%) border-box
    `,
  },
};

// Автор, которого нет в списке выше, всё равно получает авторскую вёрстку —
// просто в оформлении по умолчанию.
const DEFAULT_AUTHOR_CARD_STYLE = AUTHOR_CARD_STYLE.husnora;

export const FoodHomeCard = ({ product, isNavigate }: IProps) => {
  const navigate = useNavigate();
  const { addToBasket } = useAddToBasket();
  const { decreaseFromBasket } = useDecreaseFromBasket();
  const lang = useAppLanguage();
  const [imgLoading, setImgLoading] = useState(false);

  const modifierGroups = product.modifier_groups;

  // Внутрь товара имеет смысл проваливаться, только если там есть что выбрать.
  // У комбо все позиции зафиксированы (min=max), выбора нет — такой товар
  // кладём в корзину прямо с карточки, вместе с его составом.
  const hasChoice = useMemo(
    () => getChoosableGroups(modifierGroups).length > 0,
    [modifierGroups],
  );

  const defaultModifiers = useMemo(
    () =>
      hasChoice
        ? []
        : buildModifierPayload(
            modifierGroups ?? [],
            getDefaultSelections(modifierGroups),
          ),
    [hasChoice, modifierGroups],
  );

  // Ключ строим тем же helper'ом, что и детальная: разойдись они — счётчик на
  // карточке не нашёл бы свою строку корзины, а комбо добавлялось бы дважды.
  // У товара с выбором ключ остаётся простым: какой именно набор лежит
  // в корзине, карточка не знает.
  const cartKey = useMemo(
    () =>
      hasChoice
        ? String(product.id)
        : buildCartKey(product.id, getDefaultSelections(modifierGroups)),
    [hasChoice, modifierGroups, product.id],
  );

  const unitPrice =
    (Number(product.org_price) || 0) + getModifierTotal(defaultModifiers);

  const basketItem = useSelector((state: RootState) =>
    state.basket.items.find(
      (item) => item.id === product.id && item.cartKey === cartKey,
    ),
  );
  const count = useMemo(() => basketItem?.quantity || 0, [basketItem]);
  const isLimitReached = useCartLimitReached(product);
  const isAuthored = Boolean(product.author);
  const authorStyle = isAuthored
    ? (AUTHOR_CARD_STYLE[product.author as string] ??
      DEFAULT_AUTHOR_CARD_STYLE)
    : undefined;

  const { t } = useTranslation();

  const handleAdd = async (e: React.MouseEvent) => {
    e.stopPropagation();
    impactOccuer("light");
    if (hasChoice) {
      navigate(`/${product.id}`);
      return;
    }
    await addToBasket({
      product,
      cartKey,
      unitPrice,
      selectedModifiers: defaultModifiers,
    });
  };

  const handleRemove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    impactOccuer("light");
    await decreaseFromBasket({
      product_id: product.id,
      cartKey,
      remove_all: false,
    });
  };

  const handleNavigate = () => {
    impactOccuer("soft");
    if (!isNavigate) {
      navigate(`/${product.id}`);
    }
  };

  return (
    <Card
      className={cn("flex flex-col w-full rounded-[20px] shadow-none p-2", {
        "bg-fill-base border-none gap-y-1": !isAuthored,
        // Отличие авторской карточки — градиентные заливка и рамка, а картинка
        // растягивается под края карточки без отступов.
        "border border-solid border-transparent gap-y-[6px] overflow-hidden":
          isAuthored,
      })}
      style={authorStyle}
      onClick={handleNavigate}
    >
      <div className="relative">
        <div
          className={cn(
            "flex items-center justify-center relative",
            // Авторская картинка — квадрат во всю ширину карточки (167.5×167.5
            // на 375px), поэтому отступы карточки гасим отрицательными полями.
            isAuthored ? "-mx-2 -mt-2 w-auto aspect-square" : "w-full h-[140px]",
          )}
        >
          <ProductImage
            loading="lazy"
            onLoad={() => setImgLoading(true)}
            src={product.menu_image}
            alt="background"
            placeholderClassName={isAuthored ? "p-8" : "p-6"}
            className={cn(
              "w-full h-full transition-opacity duration-300 ease-in-out",
              {
                "max-h-[140px] rounded-xl object-contain": !isAuthored,
                "rounded-[12px] object-cover": isAuthored,
                "opacity-0": !imgLoading,
                "opacity-100": imgLoading,
              },
            )}
          />
        </div>
        {/* {isActive && percent && (
          <div className="absolute top-0 left-[0.5px] text-caption-xs-medium flex items-center justify-center px-2 text-text-white rounded-full bg-fill-danger min-h-6">
            {percent}
          </div>
        )} */}
        {/* {isActive && time && (
          <div className="absolute top-0 left-[0.5px] text-caption-xs-medium flex flex-row items-center gap-x-1 justify-center px-2 text-text-white rounded-full bg-fill-danger min-h-6">
            <ClockIcon />
            {formatTime(secondsLeft)}
          </div>
        )} */}
        {/* {isSpice && (
          <div className="absolute top-0 right-[0.5px] flex items-center justify-center p-1 text-text-white rounded-full bg-fill-danger h-6 w-6">
            <PepperIcon />
          </div>
        )} */}
        {/* {isVegan && (
          <div className="absolute top-0 right-[0.5px] flex items-center justify-center p-1 text-text-white rounded-full bg-brand-green w-6 h-6">
            <VeganIcon />
          </div>
        )} */}
      </div>

      {/* mt-auto — прижимаем текст к кнопке внизу: карточки в ряду растягиваются
          по самой высокой (например, авторской), и без этого под текстом
          повисала пустота. */}
      <div
        className={cn("mt-auto flex flex-col px-[2px] pb-[8px]", {
          "gap-y-1": !isAuthored,
          // pt — воздух между картинкой и названием у авторских карточек.
          "gap-y-[6px] pt-[6px]": isAuthored,
        })}
      >
        {/* <div className="flex flex-row gap-x-[8px] text-caption-xs-semibold text-text-tertiary">
          {product.composition.weight && <p>{product.composition.weight} г</p>}
          {product.composition.calories && (
            <p>{product.composition.calories} ккал</p>
          )}
        </div> */}
        {product.title && (
          <p className="text-caption-sm-medium text-text-primary">
            {localized(lang, {
              ru: product.title,
              uz: product.title_uz,
              en: product.title_en,
            })}
          </p>
        )}
        {/* {oldPrice && (
          <p className="relative inline-block text-caption-sm-medium text-text-tertiary">
            {formatPrice(oldPrice)} сум
            <span className="absolute left-0 top-2 w-[48%] h-[2px] bg-fill-danger rotate-[4deg] origin-top-right" />
          </p>
        )} */}
        {product.org_price && (
          <p className="text-body-sm-semibold text-text-primary">
            {formatPrice(Number(product.org_price))} сум
          </p>
        )}
        {/* {product.description && (
          <p className="text-caption-xs-semibold text-text-primitive-yellow">
            {truncate(product.description, 20)}
          </p>
        )} */}
      </div>

      <div className="flex items-center justify-center">
        {count === 0 ? (
          <Button
            className={cn(
              "w-full h-8 rounded-full shadow-none text-body-sm-semibold text-text-primary",
              isAuthored ? "bg-fill-base" : "bg-fill-background",
            )}
            onClick={handleAdd}
          >
            {t("addToCart")}
          </Button>
        ) : (
          <div
            className={cn(
              "w-full h-8 flex py-2 flex-row items-center justify-between rounded-full shadow-none text-body-sm-semibold text-text-primary",
              isAuthored ? "bg-fill-base" : "bg-fill-background",
            )}
          >
            <Button variant="link" onClick={handleRemove}>
              <Minus className="text-text-primary" />
            </Button>
            <span className="text-body-sm-semibold">{count}</span>
            <Button
              variant="link"
              onClick={handleAdd}
              aria-disabled={isLimitReached}
              className={cn({ "opacity-40": isLimitReached })}
            >
              <PlusIcon className="text-text-primary" />
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};
